<template>
  <v-card class="collection-share-container">
    <v-card-title>Compartir {{ collection.name }}</v-card-title>
    <v-card-subtitle
      >Use los siguientes links y QR para compartir su colección<br />
      <strong
        >Por el momento las colecciones solo son accesibles desde dispositivos
        móviles (no PC)</strong
      ></v-card-subtitle
    >
    <v-card-text>
      <v-row>
        <v-col sm="6">
          <h4 class="mb-2">Link</h4>
          <v-text-field
            :value="mainLink"
            label="Dirección"
            ref="urlInput"
            class="mt-0"
            append-icon="mdi-content-copy"
            hide-details
            @click:append="copy()"
            outlined
            background-color="#fafafa"
            dense
            readonly
          />
        </v-col>
        <v-col sm="6" class="text-center">
          <h4 class="text-left">QR</h4>
          <div v-if="mainQr" class="children-inline-top">
            <div>
              <v-img
                class="d-inline-block partner-qr mb-2"
                height="100"
                width="100"
                :src="mainQr"
              />
            </div>
            <v-btn color="primary" @click="download()">Descargar</v-btn>
          </div>
        </v-col>
      </v-row>
      <div class="horizontal-separator" />
      <v-row>
        <v-col>
          <div class="mb-2 d-flex flex-row justify-space-between align-center">
            <h4>Mesas</h4>
            <v-btn color="info" @click="loadTables()" small rounded
              ><v-icon class="mr-1">mdi-refresh</v-icon>Cargar mesas</v-btn
            >
          </div>
          <p class="font-size-9e mb-1 grey--text">
            Aquí se mostraran las mesas con el mismo nombre que existan en todas
            las tiendas de la colección.
          </p>
          <div class="text-center">
            <v-skeleton-loader v-if="loadingTables" type="paragraph" />
            <template v-if="tables !== null">
              <p v-if="!tables.length">
                No hay mesas en común en todas las tiendas
              </p>
              <div v-else class="text-left">
                <v-checkbox
                  v-for="table of tables"
                  :key="table.name"
                  :label="table.name"
                  hide-details
                  dense
                  class="mt-0 col-sm-6 d-inline-block py-1"
                  :input-value="selectedTables.indexOf(table.name) >= 0"
                  @change="toggleTable(table)"
                />
                <div class="d-flex flex-row justify-end align-center">
                  <v-btn
                    color="primary"
                    :disabled="!selectedTables.length || printing"
                    @click="downloadTables(true)"
                    small
                    >Descargar QR de mesa ({{ selectedTables.length }})</v-btn
                  >
                  <!--<v-btn color="primary" class="ml-2" :disabled="!selectedTables.length || printing" @click="downloadTables(false)" small>QR de mesa simples ({{selectedTables.length}})</v-btn>-->
                </div>
              </div>
            </template>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="$emit('close')">{{ $t('close') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import ICollection from '../../interfaces/ICollection';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { saveAs } from 'file-saver';
  import { createQrBase64, createQrImage, loadImage } from '../../utils';
  import { Globals } from '../../modules/Globals';
  import { ITable } from '../../interfaces/ITable';
  import TablesService from '../../api/services/tables.service';
  import BranchesService from '../../api/services/branches.service';
  import { IBranch } from '../../interfaces/IBranch';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import JSZip from 'jszip';
  import { getTopLevelDomain } from '@/utils/domain.utils';

  @Component
  export default class CollectionShare extends Vue {
    $refs: {
      urlInput: any;
    };
    @Prop() collection: ICollection;
    branchesService = new BranchesService();
    tablesService = new TablesService();
    tables: { name: string }[] = null;
    mainLink: string = null;
    mainQr: string = null;
    loadingTables: boolean = false;
    selectedTables: string[] = [];
    printing: boolean = false;

    sizes = [
      { id: 'square', name: 'Cuadrado', img: 'qrbg', aspectRatio: 1 },
      {
        id: 'vertical',
        name: 'Vertical',
        img: 'qrbg1015',
        aspectRatio: 10 / 15,
      },
    ];
    size = this.sizes[0];

    async created() {
      this.mainLink = `${Globals.website}/collection/${this.collection.token}`;
      this.mainQr = await createQrBase64(this.mainLink, 500);
    }

    async loadTables() {
      this.loadingTables = true;
      this.selectedTables = [];
      this.tables = null;
      try {
        let promises = [];
        for (const cR of this.collection.collectionRestaurants) {
          promises.push(this.branchesService.getById(cR.branchId));
        }
        const branches: IBranch[] = await Promise.all(promises);

        promises = [];

        for (const b of branches) {
          promises.push(TablesService.getAll(b.restaurantId, b.id));
        }

        const tableGroups: ITable[][] = await Promise.all(promises);

        const tablesByName: { [name: string]: ITable[] } = {};
        this.tables = [];

        for (const tables of tableGroups) {
          for (const table of tables) {
            let nameTables = tablesByName[table.name];
            if (!nameTables) nameTables = tablesByName[table.name] = [];

            nameTables.push(table);
          }
        }

        for (const name in tablesByName) {
          const tables = tablesByName[name];
          if (tables.length === this.collection.collectionRestaurants.length) {
            this.tables.push({ name });
            this.selectedTables.push(name);
          } else if (
            tables.length > this.collection.collectionRestaurants.length
          ) {
            this.tables = null;
            this.selectedTables = [];
            vueData.addAlert(
              'Uno de los restaurantes dos mesas con el mismo nombre',
              AlertType.Error
            );
            return;
          }
        }
      } catch (e) {
        this.tables = null;
        this.selectedTables = [];
        console.error(e);
        vueData.serverError(e);
      } finally {
        this.loadingTables = false;
      }
    }

    toggleTable(table: { name: string }) {
      const index = this.selectedTables.indexOf(table.name);
      if (index < 0) {
        this.selectedTables.push(table.name);
      } else {
        this.selectedTables.splice(index, 1);
      }
    }

    copy() {
      const el = this.$refs.urlInput;
      try {
        const input = el.$el.querySelector('input');
        input.select();
        input.setSelectionRange(0, 99999);
        document.execCommand('copy');
        vueData.addAlert('Texto copiado al portapapeles', AlertType.Success);
      } catch (e) {
        console.error(e);
        vueData.addAlert('No se pudo copiar', AlertType.Error);
      }
    }

    download() {
      const qr = this.mainQr;

      let name = this.collection.name.replace(/[^a-zA-Z]+/g, '').toLowerCase();

      saveAs(qr, `${name}.png`);
    }

    async downloadTables(full: boolean) {
      this.printing = true;
      const size = this.size;

      const ppc = 118.11; //pixels per centimeter
      const canvas = <HTMLCanvasElement>document.createElement('canvas');
      const ctx = (<HTMLCanvasElement>canvas).getContext('2d');
      canvas.width = (size.id === 'square' ? 13 : 10) * ppc;
      canvas.height = (size.id === 'square' ? 13 : 15) * ppc;

      const bg = await loadImage(`assets/img/qr/${this.size.img}.png`);

      const drawAndGetUrl = async (path: string, id: string = null) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const canvasCentreHorizontal = canvas.width / 2;
        const canvasCentreVertical = canvas.height / 2;

        ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

        const qrWidth = canvas.width * (size.id === 'square' ? 0.5 : 0.65);

        const imgQRCode = await createQrImage(path, 200);
        const qrLeft = canvasCentreHorizontal - qrWidth / 2;
        const qrTop =
          size.id === 'square'
            ? canvasCentreVertical - qrWidth / 2 - qrWidth * 0.06
            : canvasCentreVertical - qrWidth * 0.72;

        ctx.drawImage(imgQRCode, qrLeft, qrTop, qrWidth, qrWidth);

        const fontSize =
          ((size.id === 'square' ? 36 : 48) / 500) * canvas.width;
        if (id) {
          ctx.font = `${fontSize}px Roboto Black`;
          ctx.textAlign = 'center';
          ctx.fillStyle = '#444';
          ctx.fillText(
            id,
            canvas.width / 2,
            qrTop + qrWidth + (size.id === 'square' ? fontSize : fontSize * 1.5)
          );
        }

        return canvas.toDataURL('image/jpeg', 0.8);
      };

      try {
        const zip = new JSZip();
        let dataURL;

        const tables = this.selectedTables;
        if (tables.length > 0) {
          const img = zip.folder(this.$tc('qr.fileName.tables'));

          for (let table of tables) {
            const code = encodeURIComponent(btoa(table));
            const name = table.replace(/[^a-zA-Z0-9]+/g, '').toLowerCase();
            dataURL = await drawAndGetUrl(
              `https://${getTopLevelDomain()}/collection/${
                this.collection.token
              }?t=${code}&ref=qr`,
              `${table}`
            );
            img.file(
              `${name}.jpg`,
              dataURL.replace('data:image/jpeg;base64,', ''),
              { base64: true }
            );
          }
        }

        const content = await zip.generateAsync({ type: 'blob' });

        const name = this.collection.name
          .replace(/[^a-zA-Z]+/g, '')
          .toLowerCase();

        saveAs(content, name + '.zip');
      } catch (ex) {
        console.error(ex);
        vueData.addAlert(this.$tc('qr.failed'), AlertType.Error);
      } finally {
        this.printing = false;
      }
    }
  }
</script>
